/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box, LinearProgress, Typography } from '@mui/material';
import type { Feature, GeoJsonProperties, GeometryObject } from 'geojson';
import { useEffect, useMemo, useState } from 'react';
import type { TimeSeriesService } from '@opengeoweb/shared';
import { CustomAccordion, ToolContainerDraggable } from '@opengeoweb/shared';
import type { MapLocation } from '@opengeoweb/webmap-react';
import { useTimeseriesTranslation } from '../../../utils/i18n';
import { useGetServiceCollectionDetails } from '../../../utils/timeseries-api/hooks';
import type { TimeseriesCollectionDetail } from '../../TimeSeriesSelect/utils';

const extractUsedServices = (
  services: TimeSeriesService[] | undefined,
  feature: Feature<GeometryObject> | undefined | null,
): TimeSeriesService[] => {
  if (
    !services ||
    !feature?.properties?.occurrences ||
    feature.properties.occurrences.length < 1
  ) {
    return [];
  }
  const usedServices: TimeSeriesService[] = [];
  feature.properties.occurrences.forEach((occ: GeoJsonProperties) => {
    if (occ?.properties?.serviceId && occ?.properties?.collectionId) {
      const service = services.find((el) => el.id === occ.properties.serviceId);
      if (service) {
        usedServices.push(service);
      }
    }
  });
  return usedServices;
};

export interface ParameterInfo {
  loading: boolean;
  parameters: string[];
}

export interface CalculatedLocationCollectionInfo {
  properties: Record<string, string>;
  parameterIndex: number;
}

export interface CalculatedLocationInfo {
  geometry: GeometryObject;
  properties: Record<string, string>;
  collections: CalculatedLocationCollectionInfo[];
}

export interface LocationInfoProps {
  feature?: Feature<GeometryObject> | null;
  mapPinLocation?: MapLocation;
  services?: TimeSeriesService[];
  isOpen: boolean;
  onClose: () => void;
  onMouseDown: () => void;
  order: number;
}

export const LocationInfo: React.FC<LocationInfoProps> = ({
  feature,
  mapPinLocation,
  services,
  isOpen,
  onClose,
  onMouseDown,
  order,
}: LocationInfoProps) => {
  const { t } = useTimeseriesTranslation();
  const [parameters, setParameters] = useState<ParameterInfo[]>([]);

  const usedServices = extractUsedServices(services, feature);
  const { data: collections } = useGetServiceCollectionDetails(
    usedServices,
    isOpen,
  );

  useEffect(() => {
    if (
      !feature?.properties?.occurrences ||
      feature.properties.occurrences.length < 1
    ) {
      setParameters((prev) => {
        const isEqual = prev.length === 0;
        return isEqual ? prev : [];
      });
    } else {
      const newParameters = feature.properties.occurrences.map(
        (occ: GeoJsonProperties): ParameterInfo => {
          if (!collections) {
            return {
              loading: true,
              parameters: [],
            };
          }
          if (!occ) {
            return {
              loading: false,
              parameters: [],
            };
          }
          if (occ.properties.parameterName) {
            return {
              loading: false,
              parameters: occ.properties.parameterName,
            };
          }
          if (
            occ.properties.serviceId &&
            occ.properties.collectionId &&
            services
          ) {
            const service = services.find(
              (el) => el.id === occ.properties.serviceId,
            );
            if (!service?.url) {
              return {
                loading: false,
                parameters: [],
              };
            }

            const collection = collections.find(
              (collection: TimeseriesCollectionDetail): boolean => {
                return collection.collectionId === occ.properties.collectionId;
              },
            );

            if (collection?.parameters) {
              return {
                loading: false,
                parameters: collection.parameters.map(
                  (parameter) =>
                    parameter.propertyLabel || parameter.propertyName,
                ),
              };
            }
            return { loading: false, parameters: [] };
          }
          return { loading: false, parameters: [] };
        },
      );

      setParameters((prev) => {
        const isEqual =
          prev.length === newParameters.length &&
          prev.every(
            (param, index) =>
              JSON.stringify(param) === JSON.stringify(newParameters[index]),
          );
        return (isEqual ? prev : newParameters) as ParameterInfo[];
      });
    }
  }, [collections, feature, services]);

  const location = useMemo(() => {
    if (!feature) {
      return undefined;
    }

    const calculated: CalculatedLocationInfo = {
      geometry: feature.geometry,
      properties: {},
      collections: [],
    };

    // Internal properties
    for (let i = 0; i < feature?.properties?.occurrences.length; i += 1) {
      const occ = feature?.properties?.occurrences[i];

      const collectionInfo: CalculatedLocationCollectionInfo = {
        properties: {
          ...occ.properties,
          locationId: occ.id,
        },
        parameterIndex: i,
      };

      calculated.collections.push(collectionInfo);
    }

    return calculated;
  }, [feature]);

  const getFormattedCoordinates = (lat: number, lon: number): string => {
    const latHemisphere = lat >= 0 ? 'N' : 'S';
    const lonHemisphere = lon >= 0 ? 'E' : 'W';

    return `${Math.abs(lat)}° ${latHemisphere}, ${Math.abs(lon)}° ${lonHemisphere}`;
  };

  const formattedCoordinates = useMemo(() => {
    if (location?.geometry.type === 'Point') {
      const latitude = location.geometry.coordinates[1];
      const longitude = location.geometry.coordinates[0];

      return getFormattedCoordinates(latitude, longitude);
    }
    if (mapPinLocation) {
      const latitude = mapPinLocation.lat;
      const longitude = mapPinLocation.lon;

      return getFormattedCoordinates(latitude, longitude);
    }
    return '';
  }, [location, mapPinLocation]);

  return (
    <ToolContainerDraggable
      data-testid="LocationInfo"
      fixTopPosition
      initialMaxHeight={800}
      isOpen={isOpen}
      minHeight={200}
      minWidth={320}
      onClose={onClose}
      onMouseDown={onMouseDown}
      order={order}
      startPosition={{ top: 150, left: 100 }}
      startSize={{ width: 400, height: 500 }}
      title={`${t('timeseries-location-info-title')}`}
    >
      <Box className="locationInfoContainer" sx={{ padding: '0 6px' }}>
        {!!location && (
          <>
            <Box sx={{ paddingLeft: '12px', paddingTop: '12px' }}>
              {location.geometry.type === 'Point' && (
                <ParameterInfoText
                  label={t('timeseries-location-info-coordinates')}
                  value={formattedCoordinates}
                />
              )}
              {Object.keys(location.properties)
                .sort()
                .map((prop) => (
                  <ParameterInfoText
                    key={prop}
                    label={t(`timeseries-location-info-${prop}`)}
                    value={
                      location.properties[prop] ||
                      t('timeseries-location-info-no-data')
                    }
                  />
                ))}
              <Typography
                color="textSecondary"
                sx={{ ...textStyle, marginLeft: '-6px' }}
                variant="caption"
              >
                {`${location.collections.length} ${location.collections.length === 1 ? t('timeseries-location-info-collection') : t('timeseries-location-info-collections')}`}
              </Typography>
            </Box>
            <CollectionList
              collections={location.collections}
              parameters={parameters}
            />
          </>
        )}
        {!location && !!mapPinLocation && (
          <Box sx={{ paddingLeft: '12px', paddingTop: '12px' }}>
            <ParameterInfoText
              label={t('timeseries-location-info-coordinates')}
              value={formattedCoordinates}
            />
          </Box>
        )}
        {!location && !mapPinLocation && (
          <Box sx={{ paddingLeft: '12px', paddingTop: '12px' }}>
            <Typography sx={textStyleWithMargin} variant="body2">
              {t('timeseries-location-info-no-location-selected')}
            </Typography>
          </Box>
        )}
      </Box>
    </ToolContainerDraggable>
  );
};

const CollectionList: React.FC<{
  collections: CalculatedLocationCollectionInfo[];
  parameters: ParameterInfo[];
}> = ({
  collections,
  parameters,
}: {
  collections: CalculatedLocationCollectionInfo[];
  parameters: ParameterInfo[];
}) => {
  return (
    <div>
      {collections.map((collection) => {
        return (
          <CollectionInfo
            collection={collection}
            key={collection.properties.collectionId}
            parameters={parameters}
          />
        );
      })}
    </div>
  );
};

const CollectionInfo: React.FC<{
  collection: CalculatedLocationCollectionInfo;
  parameters?: ParameterInfo[];
}> = ({
  collection,
  parameters,
}: {
  collection: CalculatedLocationCollectionInfo;
  parameters?: ParameterInfo[];
}) => {
  const { t } = useTimeseriesTranslation();

  return (
    <CustomAccordion
      key={collection.properties.collectionId}
      sx={{ margin: '0' }}
      title={collection.properties.collectionId}
    >
      <Box>
        {Object.keys(collection.properties)
          .sort()
          .map((prop) => (
            <ParameterInfoText
              key={prop}
              label={t(`timeseries-location-info-${prop}`)}
              value={
                collection.properties[prop] ||
                t('timeseries-location-info-no-data')
              }
            />
          ))}
        <ParameterInfoElem
          parameter={
            parameters ? parameters[collection.parameterIndex] : undefined
          }
        />
      </Box>
    </CustomAccordion>
  );
};

const ParameterInfoElem: React.FC<{ parameter?: ParameterInfo }> = ({
  parameter,
}: {
  parameter?: ParameterInfo;
}) => {
  const { t } = useTimeseriesTranslation();
  if (parameter) {
    if (parameter.loading) {
      return (
        <ParameterInfoLabel label={t('timeseries-location-info-parameters')}>
          <LinearProgress
            color="secondary"
            data-testid="loadingIndicator"
            sx={{ position: 'absolute', width: '100%', top: 32, left: 0 }}
          />
        </ParameterInfoLabel>
      );
    }
    return (
      <ParameterInfoText
        label={t('timeseries-location-info-parameters')}
        value={
          parameter.parameters.length
            ? parameter.parameters.join(', ')
            : t('timeseries-location-info-no-data')
        }
      />
    );
  }
  return (
    <ParameterInfoText
      label={t('timeseries-location-info-parameters')}
      value={t('timeseries-location-info-no-data')}
    />
  );
};

export interface ParameterInfoTextProps {
  label: string;
  value: string;
}

const textStyle = {
  lineHeight: '20px',
  letterSpacing: '0.25px',
};

const textStyleWithMargin = {
  ...textStyle,
  marginBottom: 1,
};

const ParameterInfoLabel: React.FC<{
  label: string;
  children: React.ReactNode;
}> = ({ label, children }: { label: string; children: React.ReactNode }) => {
  return (
    <Box sx={{ minWidth: '12rem', paddingRight: '1rem' }}>
      <Typography color="textSecondary" sx={textStyle} variant="caption">
        {label}
      </Typography>
      {children}
    </Box>
  );
};

const ParameterInfoText: React.FC<ParameterInfoTextProps> = ({
  label,
  value,
}: ParameterInfoTextProps) => {
  return (
    <ParameterInfoLabel label={label}>
      <Typography sx={textStyleWithMargin} variant="body2">
        {value}
      </Typography>
    </ParameterInfoLabel>
  );
};
